import { useGlobalStore } from '@highlight/ipc/react'
import * as Sentry from '@sentry/electron/renderer'
import { GlobalEvents, GlobalStore } from '@highlight/ipc'
import { UnifiedBillingState, defaultUnifiedBillingState } from '@highlight/common/types'

/**
 * Hook to read the unified billing state from GlobalStore.
 * This should be the primary way to access billing data throughout the app.
 */
export const useUnifiedBilling = (): UnifiedBillingState => {
  const [state] = useGlobalStore<UnifiedBillingState>('billing', 'state', defaultUnifiedBillingState)
  return state
}

/**
 * Emit a billing refresh event.
 * Use this after operations that change billing state (upgrade, trial start, add credits).
 */
export const refreshBilling = () => {
  GlobalEvents.emit('billing:refresh')
}

/** A fresh read goes to Orb rather than the server cache, so it needs the long ceiling. */
const BILLING_REFRESH_TIMEOUT_MS = 30_000

export const refreshBillingAndWait = async (): Promise<boolean> => {
  try {
    const published = await GlobalEvents.invokeWithTimeout<boolean>(
      'billing:refresh-and-wait',
      BILLING_REFRESH_TIMEOUT_MS,
    )
    if (!published) {
      return false
    }
    // The invoke reply and store broadcast use separate IPC messages.
    await GlobalStore.billing().syncWithMain()
    return true
  } catch (error) {
    Sentry.captureException(error)
    return false
  }
}

/**
 * Tell the main process a Stripe checkout opened in the browser, so it reconciles
 * until the purchase lands. Fulfillment happens server-side, out of this
 * process's sight, so nothing here can observe it directly.
 */
export const notifyCheckoutOpened = () => {
  GlobalEvents.emit('billing:checkout-opened')
}
